import Box from "@mui/material/Box";
import Stack from "@mui/material/Stack";
import Typography from "@mui/material/Typography";
import { IconButton, Avatar } from "@mui/material";
import { ChevronRight, Users, Building2 } from "lucide-react";

import React, {useEffect} from "react";
import backgroundImage from "../../assets/svg/company-image.svg";
import {useTranslate, useGo} from "@refinedev/core";
import axiosInstance from "../../api/axios-config";
import { useAuth } from "../../contexts/auth-context";

export interface ISalesChart {
    date: string;
    title: "Order Count" | "Order Amount";
    value: number;
}

export const NumberEmployee: React.FC = () => {
    const t = useTranslate();
    const go = useGo();
    const { user } = useAuth();

    const [company, setCompany] = React.useState<any>(null);
    const [employeeCount, setEmployeeCount] = React.useState<number>(0);
    const [isLoading, setIsLoading] = React.useState<boolean>(true);
    const [error, setError] = React.useState<string | null>(null);

    const isVigilaneUser = user?.role === 'VIGILANE';

    const getCompany = async () => {
        setIsLoading(true);

        try {
            // For VIGILANE users, get protected users count
            // Backend already filters to show only users with vigilaneId for VIGILANE role
            if (isVigilaneUser) {
                // Request with limit=1 since we only need the total count from pagination
                const {data: usersData} = await axiosInstance.get('/users?limit=1');

                // Handle both array response (legacy) and paginated response {data: [...], total: ...}
                let count = 0;
                if (Array.isArray(usersData)) {
                    count = usersData.length;
                } else if (usersData && typeof usersData.total === 'number') {
                    // Use total from paginated response (more efficient)
                    count = usersData.total;
                }
                setEmployeeCount(count);
                setCompany({ nom: 'Vigilane' });
                setError(null);
                setIsLoading(false);
                return;
            }

            // Use user data from auth context instead of making another API call
            const entrepriseId = user?.entrepriseId;

            if (!entrepriseId) {
                setIsLoading(false);
                return;
            }

            // Step 2: Use the new endpoint to get company details and count in one request
            const {data: companyData} = await axiosInstance.get(`/company/count/${entrepriseId}`);

            if (companyData && typeof companyData === 'object') {
                // Set company details
                setCompany(companyData.company);
                // Set employee count
                setEmployeeCount(companyData.count);
                setError(null);
            }

        } catch (err) {
            setError("error");
        } finally {
            setIsLoading(false);
        }
    };

    useEffect(() => {
        // Only fetch if we have user data
        if (user) {
            getCompany();
        }
    }, [user]);

    return (
        <Box
            sx={{
                height: "100%",
                background: `linear-gradient(135deg, #4CAF50 0%, #45a049 100%)`,
                width: '100%',
                position: 'relative',
                overflow: 'hidden',
                display: 'flex',
                flexDirection: 'column',
                '&::before': {
                    content: '""',
                    position: 'absolute',
                    top: 0,
                    right: 0,
                    bottom: 0,
                    left: 0,
                    background: `url(${backgroundImage})`,
                    backgroundRepeat: "no-repeat",
                    backgroundPosition: "right center",
                    backgroundSize: "contain",
                    opacity: 0.2,
                    zIndex: 1
                }
            }}
        >
            {/* Navigation Button */}
            <IconButton
                onClick={() => go({ to: "/company" })}
                sx={{
                    position: 'absolute',
                    top: 16,
                    right: 16,
                    zIndex: 3,
                    backgroundColor: 'rgba(255, 255, 255, 0.2)',
                    color: 'white',
                    width: 32,
                    height: 32,
                    '&:hover': {
                        backgroundColor: 'rgba(255, 255, 255, 0.3)',
                    },
                }}
            >
                <ChevronRight size={16} />
            </IconButton>

            <Stack sx={{ position: 'relative', zIndex: 2, p: 3, flex: 1 }}>
                <Stack direction="row" alignItems="center" spacing={2} sx={{ mb: 1 }}>
                    {/* Company Icon/Logo */}
                    {company?.logo ? (
                        <Avatar
                            src={company.logo}
                            sx={{
                                width: 40,
                                height: 40,
                                bgcolor: 'rgba(255, 255, 255, 0.2)',
                                border: '2px solid',
                                borderColor: 'primary.main',
                            }}
                        />
                    ) : (
                        <Building2 size={40} color="rgba(255, 255, 255, 0.9)" />
                    )}
                    <Box>
                        <Typography
                            variant="h6"
                            sx={{
                                color: "rgba(255, 255, 255, 0.95)",
                                fontWeight: 600,
                                fontFamily: 'Inter, sans-serif',
                                letterSpacing: '-0.01em',
                            }}
                        >
                            {company?.nom || t("dashboard.company.default")}
                        </Typography>
                    </Box>
                </Stack>

                {isLoading && (
                    <Typography sx={{ color: "rgba(255, 255, 255, 0.7)", fontSize: 14, mt: 2 }}>
                        {t("dashboard.company.loading")}
                    </Typography>
                )}
                {error && (
                    <Typography sx={{ color: "rgba(255, 255, 255, 0.7)", fontSize: 14, mt: 2 }}>
                        {t("dashboard.company.error")}
                    </Typography>
                )}

                <Box sx={{ mt: 'auto' }}>
                    <Stack direction="row" alignItems="center" spacing={2} sx={{ mb: 1 }}>
                        {/* Employee Icon */}
                        <Users size={40} color="rgba(255, 255, 255, 0.9)" />
                        <Typography
                            variant="h4"
                            sx={{
                                color: "#fff",
                                fontWeight: 700,
                                mb: 0.5,
                                fontFamily: 'Inter, sans-serif'
                            }}
                        >
                            {employeeCount || "0"}
                        </Typography>
                    </Stack>
                    <Typography
                        variant="h6"
                        sx={{
                            color: "rgba(255, 255, 255, 0.9)",
                            fontWeight: 500,
                            fontFamily: 'Inter, sans-serif'
                        }}
                    >
                        {t("dashboard.numberEmployee-protected")}
                    </Typography>
                </Box>
            </Stack>
        </Box>
    );
};
